import {logger} from 'common'
import {NextParsedUrlQuery} from 'next/dist/server/request-meta'
import {useRef} from 'react'

import {analytics} from '@/utils/tracking/analytics'
import {TRACKED_URI_PARAMS} from '@/utils/tracking/constants'

export const useLogFirstFormInteraction = (message: string) => {
  // Because we are using <Formik> component instead of `useFormik` in forms, we can not handle
  // this inside `useEffect` unless we extract <Formik> children into a separate component.
  // Change if <Formik> was refactored to useFormik.
  const tracked = useRef(false)

  return (isDirty: boolean) => {
    if (isDirty && !tracked.current) {
      analytics.track(message, {})
      tracked.current = true
    }
  }
}

const getExpiryRecord = (gclidParam: string) => {
  const expiryPeriod = 90 * 24 * 60 * 60 * 1000

  return JSON.stringify({
    value: gclidParam,
    expiryDate: new Date().getTime() + expiryPeriod,
  })
}

const processUriParam = (
  item: [string, any],
  idx: number,
  list: [string, any][],
): [string, string] | null => {
  const [paramKey, paramValue] = item

  if (typeof paramValue !== 'string' || paramKey === 'gclsrc') {
    return null
  }

  if (paramKey === 'gclid') {
    const gclsrc = list.find(([key]) => key === 'gclsrc')
    if (!gclsrc || (typeof gclsrc[1] === 'string' && gclsrc[1].indexOf('aw') !== -1)) {
      return [paramKey, getExpiryRecord(paramValue)]
    } else {
      return null
    }
  }

  return [paramKey, paramValue]
}

export const saveCampaignsData = (uriParams: NextParsedUrlQuery) => {
  if (typeof window !== 'undefined') {
    try {
      Object.entries(uriParams)
        .filter(([paramKey]) => TRACKED_URI_PARAMS.includes(paramKey))
        .map(processUriParam)
        .forEach((item) => {
          if (item) {
            localStorage.setItem(...item)
          }
        })
    } catch (e) {
      logger.warn(e, 'saveCampaignsData: could not write campaign params to localStorage')
    }
  }
}

export const getCampaignsData = (): Record<string, string | null> => {
  if (typeof window !== 'undefined') {
    try {
      return TRACKED_URI_PARAMS.reduce((acc, paramKey) => {
        const paramValue = localStorage.getItem(paramKey)
        if (typeof paramValue === 'string') {
          if (paramKey === 'gclid') {
            const gclidRecord = JSON.parse(paramValue)
            if (gclidRecord && new Date().getTime() < gclidRecord?.expiryDate) {
              return {...acc, [paramKey]: gclidRecord.value}
            } else {
              return acc
            }
          }
          return {...acc, [paramKey]: paramValue}
        } else {
          return acc
        }
      }, {})
    } catch (e) {
      logger.warn(e, 'getCampaignsData: could not read campaign params from localStorage')
    }
  }
  return {}
}

export const trackClickOnContactEmail = (parameters: Record<string, string>) =>
  analytics.track('Click on support@tramatm.com', parameters)
